import { Link } from "react-router";
import { Avatar, EmptyState, Icon, IconButton, Sheet, SkeletonText } from "@astrum/ui";
import { AttachmentList } from "./AttachmentList.js";
import "./info-panel.css";

/**
 * The toggleable right-side panel that sits beside a channel or a project.
 *
 * One component for both because they answer the same question — who is
 * involved, what has been shared, what is this linked to — and a user who
 * learns it in chat should not have to learn it again in Work.
 *
 * The panel is a peer of the content rather than an overlay on desktop: an
 * overlay would hide the thing you opened it to cross-reference. Below the
 * breakpoint there is no room for that, so it becomes a Sheet.
 */

export type InfoPanelMember = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  role: string;
  department?: string | null;
  jobTitle?: string | null;
};

export type InfoPanelFile = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploaderName: string;
  createdAt: string;
};

export type InfoPanelLink = {
  label: string;
  to: string;
  icon: "work" | "chat" | "calendar" | "document";
};

export interface InfoPanelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Free text shown at the top — a channel topic or a project summary. */
  about?: string | null;
  members: InfoPanelMember[];
  /** Where uploads attach. Omit to hide the files section entirely. */
  fileScope?: { channelId?: string; projectId?: string; taskId?: string };
  /** Cross-links to related surfaces, e.g. a channel's project. */
  links?: InfoPanelLink[];
  loading?: boolean;
  /** Rendered above Members — used by project detail for health and dates. */
  children?: React.ReactNode;
  canUpload?: boolean;
  canManageMembers?: boolean;
  onManageMembers?: () => void;
  /** Removes someone. Omit to hide the control entirely. */
  onRemoveMember?: (member: InfoPanelMember) => void;
  /** The owner, who cannot be removed without transferring ownership first. */
  ownerId?: string;
}

function PanelBody({
  about,
  members,
  fileScope,
  links,
  loading,
  children,
  canUpload,
  canManageMembers,
  onManageMembers,
  onRemoveMember,
  ownerId,
}: Omit<InfoPanelProps, "open" | "onClose" | "title">) {

  if (loading) {
    return (
      <div className="info-panel__section">
        <SkeletonText lines={5} />
      </div>
    );
  }

  return (
    <>
      {children}

      {about && (
        <section className="info-panel__section">
          <h4>About</h4>
          <p className="info-panel__about">{about}</p>
        </section>
      )}

      <section className="info-panel__section">
        <div className="info-panel__section-head">
          <h4>Members · {members.length}</h4>
          {canManageMembers && onManageMembers && (
            <IconButton
              icon={<Icon name="plus" size={14} />}
              label="Add a member"
              size="sm"
              onClick={onManageMembers}
            />
          )}
        </div>

        {members.length === 0 ? (
          <p className="info-panel__empty">Nobody has been added yet.</p>
        ) : (
          <ul className="info-panel__members">
            {members.map((member) => (
              <li key={member.id} className="info-panel__member">
                <Avatar name={member.displayName} src={member.avatarUrl ?? undefined} size={28} />
                <div className="info-panel__member-text">
                  <strong>{member.displayName}</strong>
                  <span>{member.jobTitle ?? member.department ?? member.role}</span>
                </div>

                {/* The owner cannot be removed without transferring ownership
                    first; the server refuses it, so it is not offered. */}
                {canManageMembers && onRemoveMember && member.id !== ownerId && (
                  <button
                    type="button"
                    className="info-panel__member-remove"
                    aria-label={`Remove ${member.displayName}`}
                    title={`Remove ${member.displayName}`}
                    onClick={() => onRemoveMember(member)}
                  >
                    <Icon name="close" size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {fileScope && (
        <section className="info-panel__section">
          <AttachmentList
            scope={fileScope}
            canUpload={canUpload}
            variant="panel"
            emptyText="No files shared yet."
          />
        </section>
      )}

      {links && links.length > 0 && (
        <section className="info-panel__section">
          <h4>Linked</h4>
          <ul className="info-panel__links">
            {links.map((link) => (
              <li key={link.to}>
                <Link className="info-panel__link" to={link.to}>
                  <Icon name={link.icon} size={14} />
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

export function InfoPanel({ open, onClose, title = "Details", ...body }: InfoPanelProps) {
  return (
    <>
      <aside className={`info-panel ${open ? "is-open" : ""}`} aria-label={title}>
        <div className="info-panel__header">
          <h3>{title}</h3>
          <IconButton icon={<Icon name="close" size={15} />} label="Close details" onClick={onClose} />
        </div>
        <div className="info-panel__scroll">
          <PanelBody {...body} />
        </div>
      </aside>

      {/* Below the breakpoint the panel has nowhere to sit beside the content,
          so the same body is presented as a Sheet. */}
      <div className="info-panel--mobile">
        <Sheet open={open} onClose={onClose} title={title}>
          <div className="info-panel__sheet-body">
            <PanelBody {...body} />
          </div>
        </Sheet>
      </div>
    </>
  );
}

/** Convenience for the empty state when nothing is selected. */
export function InfoPanelEmpty({ message }: { message: string }) {
  return <EmptyState icon="info" title="Nothing selected" description={message} />;
}
